import { Alert, AlertTitle, Box, Paper, useTheme } from "@mui/material";
import * as React from "react";

import { EventDetails } from "@/shared/components/events/EventDetails";
import { EventRecord, normalizeEventRecord } from "@/shared/domain/events";
import { useEventMetadataKeys } from "@/shared/domain/events/hooks";
import { useEventTablePreferences } from "@/shared/domain/users/hooks";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { SearchQueryBody } from "@/types";

import { RobotoDataGrid } from "../RobotoDataGrid";

import { ColumnsModal } from "./ColumnsModal";
import { TablePageState, tablePageReducer } from "./stateReducer";
import { createRows, mergeMetadataColumns } from "./tableLogic";
import type { BaseTableProps } from "./tableProps";

const doNothing = () => {};

const possibleStandardEventColumns = [
  "event_id",
  "name",
  "description",
  "start_time",
  "end_time",
  "created",
  "created_by",
  "modified",
  "modified_by",
  "tags",
];

const sortableColumns = new Set<string>();

const initialQuery: SearchQueryBody = {
  sort_by: "created",
  sort_direction: "DESC",
};

const createTableState = (
  columns: string[],
  rowsPerPage: number = 25,
): TablePageState => ({
  modalOpen: false,
  rightClickedRow: null,
  sidebar: { isOpen: false, dataset: null },
  columns,
  rowsPerPage: rowsPerPage as 10 | 25 | 50,
  snackbar: { isOpen: false, message: "" },
  sortColumnIndex: 1,
  sortOrder: "descending",
  lastSearchQuery: initialQuery,
  selectedRows: new Set<string>(),
  preferencesLoaded: false,
});

interface EventResultsTableProps extends BaseTableProps<EventRecord> {}

export const EventResultsTable: React.FC<EventResultsTableProps> = ({
  data,
  dataCount,
  loading,
  containerStyle,
  fetchMoreData,
}) => {
  const theme = useTheme();
  // Session events arrive without associations; normalizing gives
  // every row the shape the expanded event details read.
  const events = React.useMemo(() => data.map(normalizeEventRecord), [data]);
  const {
    preferences,
    saveColumns,
    saveRowsPerPage,
    isLoading: isLoadingPreferences,
    error,
    clearError,
  } = useEventTablePreferences();

  // Initialize state with loaded preferences
  const [state, dispatch] = React.useReducer(
    tablePageReducer,
    createTableState(preferences.columns, preferences.rowsPerPage),
  );

  const { data: metadataKeys } = useEventMetadataKeys();

  const [page, setPage] = React.useState(0);

  const eventsById = new Map<string, EventRecord>(
    events.map((event) => {
      return [event.event_id, event];
    }),
  );

  const pageData = React.useMemo(() => {
    return events.slice(
      page * state.rowsPerPage,
      page * state.rowsPerPage + state.rowsPerPage,
    );
  }, [events, state.rowsPerPage, page]);

  const { rows, standardColumns, metadataColumns } = React.useMemo(() => {
    const possibleMetadataColumns: string[] =
      mergeMetadataColumns(pageData, metadataKeys ?? []) ?? [];

    const rows = createRows(pageData, state.columns, possibleMetadataColumns);

    // Sort standard columns alphabetically
    const standardColumns = [...possibleStandardEventColumns].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    // Sort metadata columns alphabetically
    const metadataColumns = [...possibleMetadataColumns].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    return { rows, standardColumns, metadataColumns };
  }, [pageData, state.columns, metadataKeys]);

  const noSearchResults =
    Object.keys(state.lastSearchQuery).length > 0 && events.length === 0;

  React.useEffect(() => {
    if (!isLoadingPreferences && !state.preferencesLoaded) {
      dispatch({
        type: "LOAD_PREFERENCES",
        payload: {
          columns: preferences.columns,
          rowsPerPage: preferences.rowsPerPage as 10 | 25 | 50,
        },
      });
    }
  }, [
    isLoadingPreferences,
    preferences.columns,
    preferences.rowsPerPage,
    state.preferencesLoaded,
  ]);

  if (isLoadingPreferences) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
      }}
    >
      <Box
        sx={{
          flex: 1,
          maxWidth: "100%",
        }}
      >
        <Paper
          sx={{
            mt: theme.spacing(2),
          }}
        >
          {error && (
            <Alert
              severity="error"
              onClose={clearError}
              sx={{ mb: theme.spacing(2) }}
            >
              {error instanceof RobotoDomainException
                ? error.message
                : "An unexpected error occurred while saving your preferences. Please try again later."}
            </Alert>
          )}

          {noSearchResults && (
            <Alert severity="info">
              <AlertTitle>Events</AlertTitle>
              <>
                No events match your search criteria. Try changing your search
                terms and try again.
              </>
            </Alert>
          )}

          {!noSearchResults && (
            <>
              <RobotoDataGrid
                sortableColumns={sortableColumns}
                sortingOrder={state.sortOrder}
                sortingColumnIndex={state.sortColumnIndex}
                page={page}
                rowsPerPage={state.rowsPerPage}
                nextPageButtonDisabled={
                  dataCount !== undefined
                    ? (page + 1) * state.rowsPerPage >= dataCount
                    : true
                }
                loading={loading}
                containerStyle={containerStyle}
                columnNames={state.columns}
                rows={rows}
                totalRowCount={dataCount}
                onPageChange={(newPage, rowsPerPage) => {
                  if ((newPage + 2) * rowsPerPage >= events.length) {
                    // Request more pages of data from the backend
                    // as we approach the last page of cached results.
                    // 2 pages prior seemed to work well experimentally.
                    fetchMoreData?.();
                  }
                  setPage(newPage);
                }}
                onRowsPerPageChange={(_page, newRowsPerPage) => {
                  setPage(0);
                  dispatch({
                    type: "SET_TABLE_CONFIG",
                    payload: {
                      rowsPerPage: newRowsPerPage,
                    },
                  });

                  void saveRowsPerPage(newRowsPerPage);
                }}
                onColumnSortClicked={doNothing}
                onAddColumnClick={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: true });
                }}
                onRowSingleClick={doNothing}
                onRowDoubleClick={doNothing}
                onSelectedRowsChange={doNothing}
                isRowSelectable={false}
                isRowExpandable={true}
                clearSelectedRowsToggle={false}
                expandableContent={(rowId: string) => {
                  const event = eventsById.get(rowId);
                  if (event) {
                    return (
                      <Box
                        sx={{
                          padding: theme.spacing(3, 2),
                        }}
                      >
                        <EventDetails event={event} readonly={true} />
                      </Box>
                    );
                  }
                }}
              />

              <ColumnsModal
                open={state.modalOpen}
                handleClose={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: false });
                }}
                standardColumns={standardColumns}
                metadataColumns={metadataColumns}
                currentColumns={state.columns}
                onSave={(updatedColumns) => {
                  dispatch({ type: "SAVE_COLUMNS", payload: updatedColumns });
                  void saveColumns(updatedColumns);
                }}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
